// Score comparison bar showing "Your Score" vs "Age Average"
// Color-coded based on comparison

import { motion } from 'framer-motion'
import type { MetricStatus } from '@/types/analysis'
import { getStatusColor } from '@/types/analysis'

interface ScoreComparisonBarProps {
  userScore: number      // 0-100 (higher = better condition)
  ageAverage: number     // 0-100 (higher = better condition)
  status: MetricStatus
  showLabels?: boolean
  animated?: boolean
}

export function ScoreComparisonBar({
  userScore,
  ageAverage,
  status,
  showLabels = true,
  animated = true,
}: ScoreComparisonBarProps) {
  // Display scores directly (higher = better skin condition)
  const userDisplay = userScore
  const avgDisplay = ageAverage
  const statusColor = getStatusColor(status)

  // Calculate difference (positive = better than average)
  const diff = userScore - ageAverage
  const diffText =
    diff > 5
      ? `${Math.abs(Math.round(diff))}% better`
      : diff < -5
        ? `${Math.abs(Math.round(diff))}% lower`
        : 'About average'

  return (
    <div className="space-y-3">
      {/* Your Score */}
      <div>
        {showLabels && (
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-sm font-medium text-gray-700">Your Score</span>
            <span className="text-sm font-semibold" style={{ color: statusColor }}>
              {userDisplay}%
            </span>
          </div>
        )}
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: statusColor }}
            initial={animated ? { width: 0 } : undefined}
            animate={{ width: `${userDisplay}%` }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Age Average */}
      <div>
        {showLabels && (
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-sm text-gray-500">Age Average</span>
            <span className="text-sm text-gray-500">{avgDisplay}%</span>
          </div>
        )}
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gray-300"
            initial={animated ? { width: 0 } : undefined}
            animate={{ width: `${avgDisplay}%` }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
          />
        </div>
      </div>

      {/* Comparison text */}
      <p className="text-xs text-center" style={{ color: statusColor }}>
        {diffText} than your age group
      </p>
    </div>
  )
}

// Compact inline version
interface ScoreComparisonInlineProps {
  userScore: number
  ageAverage: number
  status: MetricStatus
}

export function ScoreComparisonInline({ userScore, ageAverage, status }: ScoreComparisonInlineProps) {
  // Display scores directly (higher = better skin condition)
  const userDisplay = userScore
  const avgDisplay = ageAverage
  const statusColor = getStatusColor(status)
  const diff = userScore - ageAverage

  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="flex items-center gap-1.5">
        <span className="font-medium" style={{ color: statusColor }}>
          {userDisplay}%
        </span>
        <span className="text-gray-400">You</span>
      </div>
      <span className="text-gray-300">|</span>
      <div className="flex items-center gap-1.5">
        <span className="text-gray-500">{avgDisplay}%</span>
        <span className="text-gray-400">Avg</span>
      </div>
      {Math.abs(diff) > 5 && (
        <span
          className="text-xs px-2 py-0.5 rounded-full"
          style={{
            backgroundColor: `${statusColor}15`,
            color: statusColor,
          }}
        >
          {diff > 0 ? `+${Math.abs(Math.round(diff))}%` : `-${Math.abs(Math.round(diff))}%`}
        </span>
      )}
    </div>
  )
}
